import React from 'react';
import { Search, Plus, DollarSign, FileText, Settings, Printer } from 'lucide-react';

export const ClaimsSection = ({ isSuperAdmin }) => (
  <>
    <h3 className="text-lg font-semibold mb-3">Claim Submission & Management</h3>

    <div className="mb-6">
      <h4 className="font-semibold mb-2 flex items-center gap-2">
        <Search className="w-4 h-4" />
        1. Patient Search
      </h4>
      <p className="text-sm mb-2">Search for patients using either:</p>
      <ul className="list-disc list-inside space-y-1 text-sm ml-4">
        <li><strong>Serial Number</strong>: Patient's unique identifier (e.g., SEC-2413-1)</li>
        <li><strong>Family ID</strong>: To view all family members (e.g., SEC-2413)</li>
      </ul>
      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-2 text-sm">
        <strong>Note:</strong> Suspended patients will be visible but cannot have claims submitted.
      </div>
    </div>

    <div className="mb-6">
      <h4 className="font-semibold mb-2 flex items-center gap-2">
        <Plus className="w-4 h-4" />
        2. Adding Claim Items
      </h4>
      <ul className="list-disc list-inside space-y-1 text-sm ml-4">
        <li>Use the item search box to find services by Item ID or Name</li>
        <li>Search is case-insensitive (e.g., "consultation" finds "Consultation")</li>
        <li>Click "Add to Claim" button for each item</li>
        <li>Adjust quantity if needed</li>
        <li>Remove items using the trash icon</li>
      </ul>
    </div>

    <div className="mb-6">
      <h4 className="font-semibold mb-2 flex items-center gap-2">
        <DollarSign className="w-4 h-4" />
        3. Balance Verification
      </h4>
      <p className="text-sm mb-2">Before submission, the system checks:</p>
      <ul className="list-disc list-inside space-y-1 text-sm ml-4">
        <li>Family has sufficient remaining balance</li>
        <li>Claim cost is automatically converted to USD if needed</li>
        <li>Warning appears if balance is insufficient</li>
      </ul>
    </div>

    <div className="mb-6">
      <h4 className="font-semibold mb-2 flex items-center gap-2">
        <FileText className="w-4 h-4" />
        4. Claim Submission
      </h4>
      <p className="text-sm mb-2">Click "Submit Claim" to process. The system will:</p>
      <ul className="list-disc list-inside space-y-1 text-sm ml-4">
        <li>Deduct cost from family balance</li>
        <li>Create claim with PENDING status</li>
        <li>Display success message with claim ID</li>
        <li>Clear the form for next claim</li>
      </ul>
    </div>

    {isSuperAdmin && (
      <div className="mb-6">
        <h4 className="font-semibold mb-2 flex items-center gap-2">
          <Settings className="w-4 h-4" />
          5. Claim Status Management (Superadmin Only)
        </h4>
        <p className="text-sm mb-2">You can change claim status to:</p>
        <ul className="list-disc list-inside space-y-1 text-sm ml-4">
          <li><strong>PENDING</strong>: Initial status, awaiting processing</li>
          <li><strong>PAID</strong>: Mark claim as paid (deducts from hospital deposit)</li>
          <li><strong>VOIDED</strong>: Cancel claim (refunds family balance)</li>
        </ul>
        <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-2 text-sm">
          <strong>Balance Adjustments:</strong> Status changes automatically adjust family and hospital balances.
        </div>
      </div>
    )}

    <div>
      <h4 className="font-semibold mb-2 flex items-center gap-2">
        <Printer className="w-4 h-4" />
        {isSuperAdmin ? '6' : '5'}. Printing Claims
      </h4>
      <p className="text-sm">Click the printer icon on any claim to generate a printable receipt with all claim details.</p>
    </div>
  </>
);
